import { isHidden } from './dom';

let aria = {};

aria.Utils = aria.Utils || {};

/**
 * @desc Set focus on descendant nodes until the first focusable element is
 *       found.
 * @param element
 *          DOM node for which to find the first focusable descendant.
 * @returns
 *  true if a focusable element is found and focus is set.
 */
aria.Utils.focusFirstDescendant = function(element, ignore) {
  for (let i = 0; i < element.childNodes.length; i++) {
    const child = element.childNodes[i];
    const matchedRadioGroup = child instanceof HTMLElement && child.className.includes(aria.Utils.specialComponents.radio);
    const focusedRadio = matchedRadioGroup && aria.Utils.focusRadioGroupItem(child);
    if (child !== ignore && (focusedRadio || aria.Utils.attemptFocus(child) || aria.Utils.focusFirstDescendant(child, ignore))) {
      return true;
    }
  }
  return false;
};

/**
 * 获取父元素下所有可聚焦的节点，如 radio-group 组件所有选项的 input 元素
 * @param {*} element
 * @param {*} ignore
 * @returns {element}
 */
aria.Utils.getFocusableChildElements = function(element, ignore, includeContainer) {
  if (!element) return [];
  const nodeList = [];
  const collectElements = (target, ignore) => {
    if (target !== ignore && aria.Utils.isFocusable(target)) {
      nodeList.push(target);
    };
  };
  const recursionGet = function(target) {
    const children = target.childNodes;
    if (includeContainer) {
      collectElements(target, ignore);
    }
    if (!children) return nodeList;
    for (let i = 0; i < children.length; i++) {
      const child = children[i];
      collectElements(child, ignore);
      const childChildren = child.childNodes;
      if (childChildren && childChildren.length > 0) {
        recursionGet(child);
      };
    };
    return nodeList;
  };
  const result = recursionGet(element);
  return result;
};

aria.Utils.focusNext = function(container, element, ignore) {
  const current = element || document.activeElement;
  const nextSibling = current.nextSibling;
  if (nextSibling && nextSibling !== ignore) {
    if (aria.Utils.attemptFocus(nextSibling)) {
      return true;
    } else if (aria.Utils.focusFirstDescendant(nextSibling)) {
      return true;
    } else {
      return aria.Utils.focusNext(container, nextSibling, ignore);
    }
  } else {
    const parent = current.parentNode;
    if (parent === container) {
      return false;
    } else {
      return aria.Utils.focusNext(container, parent, ignore);
    }
  }
};

aria.Utils.focusPrevious = function(container, element, ignore) {
  const current = element || document.activeElement;
  const previousSibling = current.previousSibling;
  if (previousSibling && previousSibling !== ignore) {
    if (aria.Utils.attemptFocus(previousSibling)) {
      return true;
    } else if (aria.Utils.focusLastDescendant(previousSibling)) {
      return true;
    } else {
      return aria.Utils.focusPrevious(container, previousSibling, ignore);
    }
  } else {
    const parent = current.parentNode;
    if (parent === container) {
      return false;
    } else {
      return aria.Utils.focusPrevious(container, parent, ignore);
    }
  }
};

/**
 * @desc Find the last descendant node that is focusable.
 * @param element
 *          DOM node for which to find the last focusable descendant.
 * @returns
 *  true if a focusable element is found and focus is set.
 */

aria.Utils.focusLastDescendant = function(element, ignore) {
  for (let i = element.childNodes.length - 1; i >= 0; i--) {
    const child = element.childNodes[i];
    if (child !== ignore && (aria.Utils.attemptFocus(child) || aria.Utils.focusLastDescendant(child, ignore))) {
      return true;
    }
  }
  return false;
};

aria.Utils.findLastFocusEl = function(element, ignore, strict = true) {
  for (let i = element.childNodes.length - 1; i >= 0; i--) {
    const child = element.childNodes[i];
    if (child !== ignore && aria.Utils.isFocusable(child, strict)) {
      return child;
    }
    const el = aria.Utils.findLastFocusEl(child, ignore, strict);
    if (el) {
      return el;
    }
  }
  return null;
};

aria.Utils.findFirstFocusEl = function(element, ignore, strict = true) {
  for (let i = 0; i < element.childNodes.length; i++) {
    const child = element.childNodes[i];
    if (child !== ignore && aria.Utils.isFocusable(child, strict)) {
      return child;
    }
    const el = aria.Utils.findFirstFocusEl(child, ignore, strict);
    if (el) {
      return el;
    }
  }
  return null;
};

/**
 * @desc Set Attempt to set focus on the current node.
 * @param element
 *          The node to attempt to focus on.
 * @returns
 *  true if element is focused.
 */
aria.Utils.attemptFocus = function(element) {
  if (!aria.Utils.isFocusable(element)) {
    return false;
  }
  try {
    element.focus();
  } catch (e) {
  }
  return (document.activeElement === element);
};

aria.Utils.isFocusable = function(element, strict) {
  if (isHidden(element)) {
    return false;
  }

  if (element.disabled || element.getAttribute('disabled')) {
    return false;
  }

  if (strict && element.tabIndex < 0) {
    return false;
  }
  if (element.tabIndex >= 0 || (element.getAttribute('tabindex') !== null && element.getAttribute('escape-dialog-trapped-focus') === null)) {
    return true;
  }

  switch (element.nodeName) {
    case 'A':
      return !!element.href && element.rel !== 'ignore';
    case 'INPUT':
      return element.type !== 'hidden' && element.type !== 'file';
    case 'BUTTON':
    case 'SELECT':
    case 'TEXTAREA':
      return true;
    default:
      return false;
  }
};

// e2e-tests/dialog/ -> test suit "radio group in dialog"
aria.Utils.focusRadioGroupItem = function(radioGroup) {
  const radios = radioGroup.querySelectorAll('[type=radio]');
  let firstAvailableRadio;
  for (let i = 0; i < radios.length; i++) {
    const radio = radios[i];
    if (!radio.disabled && radio.checked) {
      radio.focus();
      return true;
    }
    firstAvailableRadio = firstAvailableRadio || (!radio.disabled && radio);
  }
  if (firstAvailableRadio) {
    firstAvailableRadio.focus();
    return true;
  }
};

/**
 * 触发一个事件
 * mouseenter, mouseleave, mouseover, keyup, change, click 等
 * @param  {Element} elm
 * @param  {String} name
 * @param  {*} opts
 */
aria.Utils.triggerEvent = function(elm, name, ...opts) {
  let eventName;

  if (/^mouse|click/.test(name)) {
    eventName = 'MouseEvents';
  } else if (/^key/.test(name)) {
    eventName = 'KeyboardEvent';
  } else {
    eventName = 'HTMLEvents';
  }
  const evt = document.createEvent(eventName);

  evt.initEvent(name, ...opts);
  elm.dispatchEvent
    ? elm.dispatchEvent(evt)
    : elm.fireEvent('on' + name, evt);

  return elm;
};

/**
 * 根据 className 获取节点指定的父节点
 * @param {Element} target
 * @returns {Element} parentElement
 */
aria.Utils.getParentElement = function(target) {
  // 传入标签是否是DOM对象
  if (!(target instanceof HTMLElement)) return null;
  const type = target.type;
  let parentEl = target.parentElement;
  while (parentEl) {
    if (parentEl.className.includes(aria.Utils.specialComponents[type])) {
      return parentEl;
    }
    parentEl = parentEl.parentElement;
  };
  return null;
};

aria.Utils.keys = {
  backspace: 8,
  tab: 9,
  enter: 13,
  space: 32,
  left: 37,
  up: 38,
  right: 39,
  down: 40,
  esc: 27,
  home: 36,
  end: 35,
  delete: 46
};

// 特殊组件及其需要查找的父元素 className 映射
aria.Utils.specialComponents = {
  radio: 'zm-radio-group'
};

export default aria.Utils;
